import { useState, useEffect } from 'react';
import { mockBrokerUsers, mockOffices } from '../../services/mockData';

const BrokerUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    nmls: '',
    officeId: '',
    role: 'BrokerUser',
    loginType: 'Password'
  });

  useEffect(() => {
    setTimeout(() => {
      setUsers(mockBrokerUsers);
      setOffices(mockOffices.filter(o => o.brokerId === 1));
      setLoading(false);
    }, 500);
  }, []);

  const handleToggleStatus = (userId) => {
    setUsers(users.map(u => 
      u.id === userId 
        ? { ...u, status: u.status === 'Active' ? 'Disabled' : 'Active' }
        : u
    ));
  };

  const handleAddUser = () => {
    const office = offices.find(o => o.id === parseInt(formData.officeId));
    const newUser = {
      id: Math.max(...users.map(u => u.id)) + 1,
      name: formData.name,
      email: formData.email,
      nmls: formData.nmls,
      officeId: parseInt(formData.officeId),
      officeName: office?.name || '',
      role: formData.role,
      loginType: formData.loginType,
      status: 'Active',
      createdDate: new Date().toISOString().split('T')[0]
    };
    setUsers([...users, newUser]);
    setShowAddModal(false);
    resetForm();
  };

  const handleEditUser = () => {
    const office = offices.find(o => o.id === parseInt(formData.officeId));
    setUsers(users.map(u => 
      u.id === selectedUser.id
        ? {
            ...u,
            name: formData.name,
            email: formData.email,
            nmls: formData.nmls,
            officeId: parseInt(formData.officeId),
            officeName: office?.name || '',
            role: formData.role,
            loginType: formData.loginType
          }
        : u
    ));
    setShowEditModal(false);
    setSelectedUser(null);
    resetForm();
  };

  const handleResetPassword = () => {
    // Mock password reset - in production this would trigger an email
    setShowPasswordModal(false);
    setSelectedUser(null);
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      nmls: user.nmls,
      officeId: user.officeId.toString(),
      role: user.role,
      loginType: user.loginType
    });
    setShowEditModal(true);
  };

  const openPasswordModal = (user) => {
    setSelectedUser(user);
    setShowPasswordModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      nmls: '',
      officeId: '',
      role: 'BrokerUser',
      loginType: 'Password'
    });
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="page-header d-print-none">
      <div className="row g-2 align-items-center mb-3">
        <div className="col">
          <h2 className="page-title">Users Management</h2>
          <div className="text-secondary mt-1">Manage loan officers and staff</div>
        </div>
        <div className="col-auto ms-auto">
          <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            <i className="ti ti-plus me-2"></i>
            Add User
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-vcenter">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>NMLS ID</th>
                  <th>Office</th>
                  <th>Role</th>
                  <th>Login Type</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="avatar avatar-sm me-2">
                          {user.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div>{user.name}</div>
                      </div>
                    </td>
                    <td>{user.email}</td>
                    <td><span className="badge bg-blue-lt">{user.nmls}</span></td>
                    <td>{user.officeName}</td>
                    <td>
                      <span className={`badge ${user.role === 'BrokerAdmin' ? 'bg-purple-lt' : 'bg-gray-lt'}`}>
                        {user.role === 'BrokerAdmin' ? 'Admin' : 'Loan Officer'}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${
                        user.loginType === 'Both' ? 'bg-purple-lt' :
                        user.loginType === 'Federated' ? 'bg-cyan-lt' : 'bg-gray-lt'
                      }`}>
                        {user.loginType}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${user.status === 'Active' ? 'bg-green-lt' : 'bg-red-lt'}`}>
                        {user.status}
                      </span>
                    </td>
                    <td>
                      <div className="btn-group">
                        <button 
                          className="btn btn-sm btn-ghost-primary"
                          onClick={() => openEditModal(user)}
                          title="Edit user"
                        >
                          <i className="ti ti-edit"></i>
                        </button>
                        <button 
                          className="btn btn-sm btn-ghost-secondary"
                          onClick={() => openPasswordModal(user)}
                          title="Reset password"
                        >
                          <i className="ti ti-key"></i>
                        </button>
                        <button 
                          className="btn btn-sm btn-ghost-danger"
                          onClick={() => handleToggleStatus(user.id)}
                          title={user.status === 'Active' ? 'Disable user' : 'Enable user'}
                        >
                          <i className={`ti ti-${user.status === 'Active' ? 'ban' : 'check'}`}></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New User</h5>
                <button type="button" className="btn-close" onClick={() => { setShowAddModal(false); resetForm(); }}></button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label required">Name</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      placeholder="Full name"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label required">Email</label>
                    <input 
                      type="email" 
                      className="form-control" 
                      placeholder="email@example.com"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                    />
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label required">NMLS ID</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      placeholder="123456"
                      value={formData.nmls}
                      onChange={(e) => handleChange('nmls', e.target.value)}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label required">Office</label>
                    <select 
                      className="form-select"
                      value={formData.officeId}
                      onChange={(e) => handleChange('officeId', e.target.value)}
                    >
                      <option value="">Select office...</option>
                      {offices.map(office => (
                        <option key={office.id} value={office.id}>{office.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Role</label>
                    <select 
                      className="form-select"
                      value={formData.role}
                      onChange={(e) => handleChange('role', e.target.value)}
                    >
                      <option value="BrokerUser">Loan Officer</option>
                      <option value="BrokerAdmin">Admin</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Login Type</label>
                    <select 
                      className="form-select"
                      value={formData.loginType}
                      onChange={(e) => handleChange('loginType', e.target.value)}
                    >
                      <option value="Password">Password Only</option>
                      <option value="Federated">Federated Only</option>
                      <option value="Both">Both</option>
                    </select>
                  </div>
                </div>
                <div className="alert alert-info">
                  <i className="ti ti-info-circle me-2"></i>
                  User will receive an email with login instructions.
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn" onClick={() => { setShowAddModal(false); resetForm(); }}>Cancel</button>
                <button 
                  type="button" 
                  className="btn btn-primary"
                  onClick={handleAddUser}
                  disabled={!formData.name || !formData.email || !formData.nmls || !formData.officeId}
                >
                  Create User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showAddModal && <div className="modal-backdrop fade show"></div>}

      {/* Edit User Modal */}
      {showEditModal && (
        <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit User</h5>
                <button type="button" className="btn-close" onClick={() => { setShowEditModal(false); setSelectedUser(null); resetForm(); }}></button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label required">Name</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      placeholder="Full name"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label required">Email</label>
                    <input 
                      type="email" 
                      className="form-control" 
                      placeholder="email@example.com"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                    />
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label required">NMLS ID</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      placeholder="123456"
                      value={formData.nmls}
                      onChange={(e) => handleChange('nmls', e.target.value)}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label required">Office</label>
                    <select 
                      className="form-select"
                      value={formData.officeId}
                      onChange={(e) => handleChange('officeId', e.target.value)}
                    >
                      <option value="">Select office...</option>
                      {offices.map(office => (
                        <option key={office.id} value={office.id}>{office.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Role</label>
                    <select 
                      className="form-select"
                      value={formData.role}
                      onChange={(e) => handleChange('role', e.target.value)}
                    >
                      <option value="BrokerUser">Loan Officer</option>
                      <option value="BrokerAdmin">Admin</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Login Type</label>
                    <select 
                      className="form-select"
                      value={formData.loginType}
                      onChange={(e) => handleChange('loginType', e.target.value)}
                    >
                      <option value="Password">Password Only</option>
                      <option value="Federated">Federated Only</option>
                      <option value="Both">Both</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn" onClick={() => { setShowEditModal(false); setSelectedUser(null); resetForm(); }}>Cancel</button>
                <button 
                  type="button" 
                  className="btn btn-primary"
                  onClick={handleEditUser}
                  disabled={!formData.name || !formData.email || !formData.nmls || !formData.officeId}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showEditModal && <div className="modal-backdrop fade show"></div>}

      {/* Password Reset Modal */}
      {showPasswordModal && (
        <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Reset Password</h5>
                <button type="button" className="btn-close" onClick={() => { setShowPasswordModal(false); setSelectedUser(null); }}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">User</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    value={selectedUser?.name || ''}
                    disabled
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Email</label>
                  <input 
                    type="email" 
                    className="form-control" 
                    value={selectedUser?.email || ''}
                    disabled
                  />
                </div>
                <div className="alert alert-warning">
                  <div className="d-flex">
                    <div>
                      <i className="ti ti-alert-triangle me-2"></i>
                    </div>
                    <div>
                      <h4 className="alert-title">Password Reset</h4>
                      <div className="text-secondary">
                        A password reset link will be sent to <strong>{selectedUser?.email}</strong>.
                        <br />
                        The link will expire in 24 hours.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn" onClick={() => { setShowPasswordModal(false); setSelectedUser(null); }}>Cancel</button>
                <button 
                  type="button" 
                  className="btn btn-warning"
                  onClick={handleResetPassword}
                >
                  <i className="ti ti-mail me-2"></i>
                  Send Reset Link
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showPasswordModal && <div className="modal-backdrop fade show"></div>}
    </div>
  );
};

export default BrokerUsersPage;
